import { useState, useEffect, useRef } from 'react';
import timeSyncService from '../../services/TimeSyncService';

export const useDisplayTimer = (timer_state, onZeroExceeded) => {
  const [displayRemainingTime, setDisplayRemainingTime] = useState(0);
  const lastFetchTimeRef = useRef(0);

  useEffect(() => {
    if (!timer_state) return;

    const { state, endsAt, pausedAt, remaining: legacyRemaining } = timer_state;

    const updateDisplayTime = () => {
      if (state === 'play' && endsAt) {
        const endsAtTime = new Date(endsAt).getTime();
        const now = timeSyncService.getNow();
        // Add 50ms tolerance to prevent Math.ceil from rounding up 30.01 to 31
        const rawSeconds = Math.ceil((endsAtTime - now - 50) / 1000);
        // Math.ceilによる端数切り上げがセット済みの残り時間(legacyRemaining)を上回らないようキャップする
        const seconds = Math.max(0, legacyRemaining > 0 ? Math.min(rawSeconds, legacyRemaining) : rawSeconds);

        // --- 0:00 Flash Guard & Failsafe ---
        // If seconds is 0 but state is still 'play', it's likely the very last moment 
        // before the level advance logic updates endsAt. 
        // We skip setting 0 to maintain the 0:01 appearance until the next level loads.
        if (seconds === 0) {
          // Failsafe: if we are stuck at 0:01 for more than 3000ms past the end time,
          // it means the background throttling prevented the WebSocket update.
          if (onZeroExceeded && (now - endsAtTime > 3000)) {
            // Apply a 10-second cooldown (10000ms) to prevent infinite fetch loop spam
            if (now - lastFetchTimeRef.current > 10000) {
              // Store the true exact original Date.now() for accurate generic cooldowns
              lastFetchTimeRef.current = Date.now();
              onZeroExceeded();
            }
          }
          return;
        }

        setDisplayRemainingTime(seconds);
      } else if (pausedAt !== undefined && pausedAt !== null) {
        setDisplayRemainingTime(pausedAt);
      } else {
        // Fallback for STOP or legacy data
        setDisplayRemainingTime(legacyRemaining || 0);
      }
    };

    // Run immediately
    updateDisplayTime();

    // If playing, run interval
    if (state === 'play') {
      const interval = setInterval(updateDisplayTime, 200); // Update 5 times a second for smoothness
      return () => clearInterval(interval);
    }
  }, [timer_state, onZeroExceeded]);

  return displayRemainingTime;
};
